import React, { useState, useEffect } from "react"
import {
  Grid,
  Box,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent
} from "@mui/material"
import { Settings, Close, DeleteOutline } from "@mui/icons-material"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import AppTextField from "components/StyledComponents/AppTextField"
import AppRichTextEditor from "components/StyledComponents/AppRichTextEditor"
import Card from "components/Card/Card"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import { useDispatch, useSelector } from "react-redux"
import { superAdminAction } from "redux/superAdmin/slice/user"
import {
  updateAllPreshiftSectionsAction,
  publishPreshiftAction,
  removeItemFromSectionAction,
  getPreshiftDetailAction,
  reorderPreshiftSectionsAction
} from "redux/superAdmin/actions/user"
import RearrangeModal from "./components/RearrangeModal"
import AddItemModal from "./AddItemModal"
import ItemsSlider from "./components/ItemsSlider"
import { useHistory, useLocation } from "react-router-dom"
import CoverFlowChart from "components/CoverFlowChart"
import PreshiftUploadPdfField from "./components/PreshiftUploadPdfField"

const PreshiftDashboard = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [breadCrumbs, setBreadcrumbs] = useState([])
  const [isRearrangeOpen, setIsRearrangeOpen] = useState(false)
  const [changedSectionIds, setChangedSectionIds] = useState(new Set())
  const history = useHistory()
  const { isLoading } = useSelector((state) => state.app)
  const location = useLocation()
  const currentId = location?.state?.id
  const [previewFile, setPreviewFile] = useState(null)

  const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url || "")

  const {
    addItemConfig = { open: false, type: null, fieldId: null, editItem: null },
    selectedPreshiftDetail
  } = useSelector((state) => state.superAdmin || {})

  const isPublished = selectedPreshiftDetail?.status === "PUBLISHED"

  useEffect(() => {
    setBreadcrumbs(createBreadcrumbs())
    if (!selectedPreshiftDetail && currentId) {
      dispatch(getPreshiftDetailAction({ id: currentId }))
    }
    return () => {
      dispatch(superAdminAction.setSelectedPreshiftDetail(null))
    }
  }, [currentId])

  useEffect(() => {
    if (selectedPreshiftDetail) {
      document.title = `Preshift - ${
        selectedPreshiftDetail.date_display || selectedPreshiftDetail.date
      }`
    }
  }, [selectedPreshiftDetail])

  useEffect(() => {
    dispatch(getPreshiftDetailAction({ id: currentId }))
  }, [addItemConfig?.open])

  const handleFieldChange = (
    sectionId,
    fieldName,
    value,
    isSubField = false,
    parentFieldName = null
  ) => {
    if (!selectedPreshiftDetail) return
    const section = selectedPreshiftDetail.sections.find((s) => s.id === sectionId)
    if (!section) return
    let nextDynamicData = { ...section.dynamic_field_data }

    if (isSubField && parentFieldName) {
      nextDynamicData[parentFieldName] = {
        ...(nextDynamicData[parentFieldName] || {}),
        [fieldName]: value
      }
    } else {
      if (fieldName === "floor_plan" && value !== null && typeof value === "object") {
        nextDynamicData["floor_plan"] = value.id
        nextDynamicData["floor_plan_url"] = value
      } else {
        nextDynamicData[fieldName] = value
        if (fieldName === "floor_plan" && value === null) {
          delete nextDynamicData["floor_plan_url"]
        }
      }
    }

    const updatedSections = selectedPreshiftDetail.sections.map((s) =>
      s.id === sectionId ? { ...s, dynamic_field_data: nextDynamicData } : s
    )
    dispatch(
      superAdminAction.setSelectedPreshiftDetail({
        ...selectedPreshiftDetail,
        sections: updatedSections
      })
    )

    setChangedSectionIds((prev) => {
      const next = new Set(prev)
      next.add(sectionId)
      return next
    })
  }

  if (!selectedPreshiftDetail) return null

  const handleAddItemOpen = (sectionId, fieldId, sectionKey = null, editItem = null) => {
    handleSaveDraft(() => {
      dispatch(
        superAdminAction.setAddItemConfig({
          open: true,
          type: sectionId,
          fieldId,
          sectionKey,
          editItem
        })
      )
    })
  }

  const handleSaveDraft = (callback = onClose) => {
    if (changedSectionIds.size === 0) {
      if (callback) callback()
      return
    }

    const sectionsToUpdate = Array.from(changedSectionIds)
      ?.map((id) => {
        const section = selectedPreshiftDetail?.sections?.find((s) => s?.id == id)
        return {
          id: section?.id,
          dynamic_field_data: section?.dynamic_field_data
        }
      })
      ?.filter((item) => item?.id !== undefined)

    dispatch(
      updateAllPreshiftSectionsAction({
        preshift_id: selectedPreshiftDetail.id,
        sections: sectionsToUpdate,
        callback: () => {
          setChangedSectionIds(new Set())
          if (callback) callback()
        }
      })
    )
  }

  const handleRemoveItem = (sectionId, itemId) => {
    handleSaveDraft(() => {
      dispatch(
        removeItemFromSectionAction({
          preshift_id: selectedPreshiftDetail.id,
          section_id: sectionId,
          item_id: itemId
        })
      )
    })
  }

  const handlePublishPreshift = () => {
    handleSaveDraft(() => {
      dispatch(publishPreshiftAction({ id: selectedPreshiftDetail?.id, callback: onClose }))
    })
  }

  const onClose = () => {
    history.push("/super-admin/dashboard/preshift")
  }

  const renderField = (
    field,
    sectionId,
    dynamicData,
    isSubField = false,
    parentFieldName = null
  ) => {
    const value = dynamicData?.[field.field_name]

    switch (field.field_type) {
      case "text":
        return (
          <Grid item xs={12} md={6} key={field.id}>
            <AppTextField
              label={field.field_label}
              value={value || ""}
              fullWidth
              placeholder={`Enter ${field.field_label.toLowerCase()}`}
              onChange={(e) =>
                handleFieldChange(
                  sectionId,
                  field.field_name,
                  e.target.value,
                  isSubField,
                  parentFieldName
                )
              }
              disabled={isPublished}
            />
          </Grid>
        )
      case "number":
        return (
          <Grid item xs={12} md={3} key={field.id}>
            <AppTextField
              label={field.field_label}
              value={value || ""}
              type="number"
              fullWidth
              placeholder={`Enter ${field.field_label.toLowerCase()}`}
              onChange={(e) =>
                handleFieldChange(
                  sectionId,
                  field.field_name,
                  e.target.value,
                  isSubField,
                  parentFieldName
                )
              }
              disabled={isPublished}
            />
          </Grid>
        )
      case "rich_text":
        return (
          <Grid item xs={12} key={field.id}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
              {field.field_label}
            </Typography>
            <AppRichTextEditor
              value={value || ""}
              placeholder={`Add ${field.field_label.toLowerCase()}...`}
              onChange={(e) =>
                handleFieldChange(
                  sectionId,
                  field.field_name,
                  e.target.value,
                  isSubField,
                  parentFieldName
                )
              }
              editable={!isPublished}
            />
          </Grid>
        )
      case "image": {
        const fileUrl = typeof value === "object" ? value?.url : value
        const isImg = isImageUrl(fileUrl)
        return (
          <Grid item xs={4} key={field.id}>
            {isPublished ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                  {field.field_label}
                </Typography>
                {fileUrl ? (
                  isImg ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: 160,
                        borderRadius: 2,
                        overflow: "hidden",
                        cursor: "pointer",
                        border: `1px solid ${theme.palette.divider}`
                      }}
                      onClick={() => setPreviewFile({ url: fileUrl, isImage: true })}>
                      <img
                        src={fileUrl}
                        alt={field.field_label}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </Box>
                  ) : (
                    <iframe
                      src={`https://docs.google.com/gview?url=${encodeURIComponent(
                        fileUrl
                      )}&embedded=true`}
                      title={field.field_label}
                      style={{ width: "100%", height: 200, border: "none", display: "block" }}
                    />
                  )
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No file uploaded
                  </Typography>
                )}
              </Box>
            ) : (
              <PreshiftUploadPdfField
                title={`${field.field_label}`}
                placeholder={`Upload ${field.field_label.toLowerCase()}`}
                value={value || null}
                type={field.field_label}
                onChange={(val) =>
                  handleFieldChange(
                    sectionId,
                    field.field_name,
                    val?.url ?? val,
                    isSubField,
                    parentFieldName
                  )
                }
                disabled={isPublished}
              />
            )}
          </Grid>
        )
      }
      case "section":
        return (
          <Grid item xs={12} key={field.id}>
            <Box
              sx={{ p: 2, border: `1px dashed ${theme.palette.divider}`, borderRadius: 1, mt: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                {field.field_label}
              </Typography>
              <Grid container spacing={2}>
                {field.sub_fields?.map((subField) =>
                  renderField(subField, sectionId, value || {}, true, field.field_name)
                )}
              </Grid>
            </Box>
          </Grid>
        )
      case "items": {
        const section = selectedPreshiftDetail.sections.find((s) => s.id === sectionId)
        const sectionItems = section?.items || []
        let fieldItems = sectionItems.filter(
          (item) => item.field === field.id || (!item.field && field.order === 0)
        )

        let itemsToShow = fieldItems.map((fi) => ({
          ...fi.item_data,
          id: fi.id,
          _itemId: fi.item_id
        }))

        if (section?.dynamic_field_data?.vip_guests?.length > 0) {
          const vipItems = section?.dynamic_field_data?.vip_guests?.map((vg) => ({
            ...vg,
            id: vg?.id,
            name: vg?.name,
            image: vg?.image_url,
            category: vg?.time,
            isFallback: true
          }))
          itemsToShow = [...itemsToShow, ...vipItems]
        }

        return (
          <Grid item xs={12} key={field.id}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
              {field.field_label}
            </Typography>
            {isPublished && itemsToShow.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.6, py: 1 }}>
                No items added
              </Typography>
            ) : (
              <ItemsSlider
                items={itemsToShow}
                onAdd={() =>
                  handleAddItemOpen(sectionId, field.id, section?.section_type_detail?.section_key)
                }
                onRemove={(itemId, item) => {
                  handleRemoveItem(sectionId, item?._itemId || itemId)
                }}
                readOnly={isPublished}
              />
            )}
          </Grid>
        )
      }
      default:
        return null
    }
  }

  const renderSection = (section) => {
    if (!section.is_active) return null

    const sectionKey = section?.section_type_detail?.section_key

    if (sectionKey === "COVER_FLOW") {
      if (!section?.cover_flow_data?.length) return null
      return (
        <Card key={section.id || "cover-flow"} sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">{section?.title}</Typography>
          </Box>
          <CoverFlowChart data={section.cover_flow_data} />
        </Card>
      )
    }

    if (sectionKey === "FLOOR_PLAN") {
      const floorPlanValue = section?.dynamic_field_data?.floor_plan_url
      const floorPlanUrl = floorPlanValue?.url || floorPlanValue
      const isFloorPlanImage = isImageUrl(floorPlanUrl)
      return (
        <Card key={section.id || "floor-plan"} sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">{section?.title}</Typography>
          </Box>
          {floorPlanUrl ? (
            <Box sx={{ position: "relative" }}>
              {isFloorPlanImage ? (
                <img
                  src={floorPlanUrl}
                  alt="Floor Plan"
                  style={{
                    width: "100%",
                    maxHeight: 400,
                    objectFit: "contain",
                    display: "block",
                    borderRadius: 8
                  }}
                />
              ) : (
                <iframe
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(
                    floorPlanUrl
                  )}&embedded=true`}
                  title="Floor Plan"
                  style={{
                    width: "100%",
                    height: 500,
                    border: "none",
                    display: "block",
                    borderRadius: 8
                  }}
                />
              )}
              {!isPublished && (
                <IconButton
                  size="small"
                  onClick={() => handleFieldChange(section?.id, "floor_plan", null)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "error.main", color: "white" }
                  }}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              )}
            </Box>
          ) : !isPublished ? (
            <Box className="d-flex justify-content-center align-items-center" sx={{ width: "32%" }}>
              <PreshiftUploadPdfField
                title="Floor Plan"
                type="Floor Plan"
                value={null}
                onChange={(val) => handleFieldChange(section?.id, "floor_plan", val)}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No floor plan uploaded
            </Typography>
          )}
        </Card>
      )
    }
    if (section?.section_type_detail?.section_key === "VIP_GUESTS") {
      return (
        <Card key={section.id} sx={{ mb: 3 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", mb: 2, justifyContent: "space-between" }}>
            {section.section_type_detail?.icon_url && (
              <Box
                component="img"
                src={section.section_type_detail.icon_url}
                sx={{ width: 24, height: 24, mr: 1, filter: "invert(1)" }}
              />
            )}
            <Typography variant="h6">{section?.title}</Typography>
          </Box>

          {isPublished && !section.dynamic_field_data?.vip_guests?.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.6, py: 1 }}>
              No VIP guests added
            </Typography>
          ) : (
            <ItemsSlider
              items={section.dynamic_field_data?.vip_guests || []}
              onAdd={() =>
                handleAddItemOpen(section.id, null, section?.section_type_detail?.section_key)
              }
              onRemove={(itemId, item) => {
                handleRemoveItem(item?._itemId || itemId)
              }}
              readOnly={isPublished}
            />
          )}
        </Card>
      )
    }

    return (
      <Card key={section.id} sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          {section.section_type_detail?.icon_url && (
            <Box
              component="img"
              src={section.section_type_detail.icon_url}
              sx={{ width: 24, height: 24, mr: 1, filter: "invert(1)" }}
            />
          )}
          <Typography variant="h6">{section?.title}</Typography>
        </Box>

        <Grid container spacing={3}>
          {section.fields_metadata?.map((field) =>
            renderField(field, section?.id, section?.dynamic_field_data)
          )}
        </Grid>
      </Card>
    )
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Header title={selectedPreshiftDetail.date_display} breadCrumbs={breadCrumbs}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {!isPublished && (
              <Tooltip title="Rearrange Sections">
                <IconButton onClick={() => setIsRearrangeOpen(true)}>
                  <Settings htmlColor={theme.palette.white.main} />
                </IconButton>
              </Tooltip>
            )}
            {!isPublished && (
              <>
                <AppButton
                  variant="outlined"
                  onClick={() => handleSaveDraft()}
                  sx={{ padding: "8px 24px" }}>
                  Save Draft
                </AppButton>
                <AppButton onClick={handlePublishPreshift} styles={{ padding: "10px 24px" }}>
                  Publish
                </AppButton>
              </>
            )}
          </Box>
        </Header>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={3}>
          {isLoading ? (
            <Grid item xs={12} className="d-flex justify-content-center">
              <Typography variant="h2">
                Please wait while we getting the preshift details...
              </Typography>
            </Grid>
          ) : selectedPreshiftDetail?.sections?.length === 0 ? (
            <Grid item xs={12} className="d-flex justify-content-center">
              <Typography variant="h2">No sections found</Typography>
            </Grid>
          ) : (
            selectedPreshiftDetail?.sections
              ?.filter((section) => section?.is_active)
              ?.map((section, index) => (
                <Grid item xs={12} key={section?.id || `section-${index}`}>
                  {renderSection(section)}
                </Grid>
              ))
          )}
        </Grid>
      </Grid>

      <RearrangeModal
        open={isRearrangeOpen}
        onClose={() => setIsRearrangeOpen(false)}
        sections={selectedPreshiftDetail.sections}
        onSave={(newSections) => {
          dispatch(
            superAdminAction.setSelectedPreshiftDetail({
              ...selectedPreshiftDetail,
              sections: newSections
            })
          )
          dispatch(
            reorderPreshiftSectionsAction({
              id: selectedPreshiftDetail.id,
              payload: {
                sections: newSections.map((s, index) => ({
                  id: s.id,
                  is_active: s.is_active,
                  order: index
                }))
              }
            })
          )
        }}
      />

      <AddItemModal
        open={addItemConfig?.open || false}
        sectionId={addItemConfig?.type}
        fieldId={addItemConfig?.fieldId}
        sectionKey={addItemConfig?.sectionKey}
        preshiftId={selectedPreshiftDetail.id}
        onClose={() => dispatch(superAdminAction.resetAddItemConfig())}
        onSaveDraft={handleSaveDraft}
      />

      <Dialog
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        maxWidth="lg"
        fullWidth>
        <DialogContent sx={{ p: 0, position: "relative", minHeight: 200 }}>
          <IconButton
            onClick={() => setPreviewFile(null)}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
            <Close />
          </IconButton>
          <img
            src={previewFile?.url}
            alt="Preview"
            style={{ width: "100%", maxHeight: "85vh", objectFit: "contain", display: "block" }}
          />
        </DialogContent>
      </Dialog>
    </Grid>
  )
}

export default PreshiftDashboard
